import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * Drify cart helper.
 *
 * - window.DrifyCart.add(items, sourceElement): adds items with the Cart AJAX API,
 *   refreshes the cart drawer through the theme's CartAddEvent and opens the drawer.
 * - Plain product forms (form[action$="/cart/add"]) that are not theme
 *   product-form-components are submitted the same way instead of redirecting to /cart.
 * - Theme product forms (product page, quick add, sticky bar) open the drawer after a
 *   successful add, unless the drawer already does that itself (auto-open).
 * - When there is no cart drawer on the page (e.g. the /cart page), it falls back to /cart.
 */

const SOURCE = 'drify-cart';

/** @returns {string[]} Section ids of the cart-items components on the page. */
function getCartSectionIds() {
  const ids = new Set();
  for (const element of document.querySelectorAll('cart-items-component')) {
    if (element instanceof HTMLElement && element.dataset.sectionId) {
      ids.add(element.dataset.sectionId);
    }
  }
  return [...ids];
}

/** @returns {HTMLElement | null} */
function getDrawer() {
  return document.querySelector('cart-drawer-component');
}

/**
 * Opens the cart drawer.
 * @returns {boolean} Whether a drawer exists on the page.
 */
function openDrawer() {
  const drawer = /** @type {any} */ (getDrawer());
  if (!drawer) return false;

  const dialog = drawer.querySelector('dialog');
  if (dialog && dialog.open) return true;

  if (typeof drawer.open === 'function') {
    drawer.open();
    return true;
  }

  return false;
}

/** @returns {Promise<any | null>} */
async function fetchCart() {
  try {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    return await response.json();
  } catch (error) {
    return null;
  }
}

/**
 * Posts to /cart/add.js, then refreshes and opens the cart drawer.
 * @param {FormData | {items: Array<{id: number, quantity?: number, properties?: object, selling_plan?: number}>}} payload
 * @param {Element | null} [source] - Element that triggered the add, used as the event target.
 * @param {number} [addedQuantity]
 */
async function postAdd(payload, source, addedQuantity = 1) {
  const sections = getCartSectionIds().join(',');
  /** @type {RequestInit} */
  const init = { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } };

  if (payload instanceof FormData) {
    if (sections) {
      payload.set('sections', sections);
      payload.set('sections_url', window.location.pathname);
    }
    init.body = payload;
  } else {
    init.headers = { ...init.headers, 'Content-Type': 'application/json' };
    init.body = JSON.stringify(sections ? { ...payload, sections, sections_url: window.location.pathname } : payload);
  }

  const response = await fetch(Theme.routes.cart_add_url, init);
  const data = await response.json();

  if (!response.ok || data.status) {
    throw new Error(data.description || data.message || 'Add to cart failed');
  }

  const cart = await fetchCart();
  const target = source instanceof Element && source.isConnected ? source : document;

  target.dispatchEvent(
    new CartAddEvent(cart ?? undefined, SOURCE, {
      source: SOURCE,
      itemCount: cart ? cart.item_count : addedQuantity,
      sections: data.sections,
    })
  );

  if (!openDrawer()) {
    window.location.href = Theme.routes.cart_url;
  }

  return cart;
}

const DrifyCart = {
  /**
   * @param {Array<{id: number | string, quantity?: number}>} items
   * @param {Element | null} [source]
   */
  add(items, source) {
    const normalized = items.map((item) => ({ ...item, id: Number(item.id), quantity: Number(item.quantity) || 1 }));
    const total = normalized.reduce((sum, item) => sum + item.quantity, 0);
    return postAdd({ items: normalized }, source, total);
  },
  open: openDrawer,
};

window.DrifyCart = DrifyCart;

/** @param {string | null} action */
function isCartAddAction(action) {
  if (!action) return false;
  try {
    return /\/cart\/add(\.js)?\/?$/.test(new URL(action, window.location.href).pathname);
  } catch (error) {
    return false;
  }
}

/**
 * @param {HTMLElement | null} button
 * @param {boolean} busy
 */
function setBusy(button, busy) {
  if (!(button instanceof HTMLButtonElement || button instanceof HTMLInputElement)) return;
  button.disabled = busy;
  button.toggleAttribute('aria-busy', busy);
}

// Plain product forms (custom sections) -> AJAX add + cart drawer instead of a page redirect.
document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || event.defaultPrevented) return;
  if (!isCartAddAction(form.getAttribute('action'))) return;
  if (form.closest('product-form-component') || form.hasAttribute('data-drify-native')) return;
  if (!getDrawer()) return;

  const formData = new FormData(form);
  if (!formData.get('id')) return;

  event.preventDefault();

  const submitter = /** @type {HTMLElement | null} */ (
    event.submitter || form.querySelector('[type="submit"]')
  );
  const quantity = Number(formData.get('quantity')) || 1;

  setBusy(submitter, true);

  postAdd(formData, form, quantity)
    .catch((error) => {
      console.error(error);
      // Fall back to the normal form submission so the customer still gets feedback.
      form.setAttribute('data-drify-native', '');
      HTMLFormElement.prototype.submit.call(form);
    })
    .finally(() => setBusy(submitter, false));
});

// Theme product forms -> open the drawer after a successful add.
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const data = /** @type {any} */ (event).detail?.data;
  if (!data || data.source !== 'product-form-component' || data.didError) return;

  const drawer = getDrawer();
  if (!drawer || drawer.hasAttribute('auto-open')) return;

  openDrawer();
});
